import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePersistedBool } from "@/lib/collassabile";
import { toast } from "sonner";
import { IdCard, BellPlus, Loader2 , ChevronDown, ChevronRight } from "lucide-react";

const LEVELS = {
  scaduto: { label: "Scaduto", chip: "bg-[#9b2c2c] text-white", text: "text-[#9b2c2c]", border: "border-[#9b2c2c]" },
  "30": { label: "Entro 30 gg", chip: "bg-[#c05621] text-white", text: "text-[#c05621]", border: "border-[#c05621]" },
  "60": { label: "Entro 60 gg", chip: "bg-[#b7791f] text-white", text: "text-[#b7791f]", border: "border-[#b7791f]" },
  "90": { label: "Entro 90 gg", chip: "bg-[#1A2B4A] text-white", text: "text-[#8a6d1f]", border: "border-[#C9A84C]" },
};

function Riga({ it }) {
  const [busy, setBusy] = useState(false);
  const lv = LEVELS[it.livello] || LEVELS["90"];
  const crea = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clienti/${it.cliente_id}/scadenza-evento`, {
        documento: it.documento, data: it.scadenza, avvisa_giorni: 30,
      });
      toast.success("Promemoria creato su Google Calendar", {
        action: data.htmlLink ? { label: "Apri", onClick: () => window.open(data.htmlLink, "_blank") } : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promemoria non creato");
    } finally { setBusy(false); }
  };
  return (
    <li data-testid={`scadenza-doc-${it.cliente_id}-${it.campo}`}
      className={`flex items-center gap-3 py-2.5 pl-3 border-l-2 ${lv.border}`}>
      <div className="min-w-0 flex-1">
        <Link to="/clienti" className="text-sm font-medium text-[#1A2B4A] hover:text-[#C9A84C] transition-colors truncate block">
          {it.cliente}
        </Link>
        <div className="text-xs text-[#4a5568] truncate">{it.documento} · scade il {it.scadenza}</div>
      </div>
      <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-md shrink-0 ${lv.chip}`}>
        {it.giorni_residui < 0 ? `Scaduto da ${Math.abs(it.giorni_residui)} gg` : `${it.giorni_residui} gg`}
      </span>
      <button data-testid={`scadenza-doc-remind-${it.cliente_id}-${it.campo}`} onClick={crea} disabled={busy}
        title="Crea promemoria su Google Calendar"
        className="shrink-0 text-[#1A2B4A]/60 hover:text-[#C9A84C] transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellPlus className="w-4 h-4" />}
      </button>
    </li>
  );
}

export default function ScadenzeDocumenti({ senzaCornice }) {
  const [giorni, setGiorni] = useState(90);
  const [apertoLocale, setAperto] = usePersistedBool("home_scadenze_doc_open", true);
  const { data, isLoading } = useQuery({
    queryKey: ["scadenze-documenti", giorni],
    queryFn: async () => (await api.get("/dashboard/scadenze-documenti", { params: { giorni } })).data,
  });
  const aperto = senzaCornice ? true : apertoLocale;
  const items = data?.items || [];
  const tot = data?.totali || {};

  const azioni = (
    <select data-testid="scadenze-doc-giorni" value={giorni} onChange={(e) => setGiorni(Number(e.target.value))}
      className="text-[13px] border border-[#C6C3BA] rounded-[4px] px-3 py-1.5 bg-white focus:outline-none focus:border-[#A8863F]">
      <option value={30}>30 giorni</option>
      <option value={60}>60 giorni</option>
      <option value={90}>90 giorni</option>
      <option value={365}>1 anno</option>
    </select>
  );

  return (
    <div data-testid="scadenze-documenti-card"
      className={senzaCornice ? "" : "mb-5 bg-white border border-[#1A2B4A]/10 rounded-lg p-4 shadow-sm"}>
      {senzaCornice ? (
        <div className="flex flex-wrap items-center justify-between gap-2.5 mb-3">
          <span className="eti">{items.length} documenti nei prossimi {giorni} giorni</span>
          {azioni}
        </div>
      ) : (
      <div className={`flex items-center justify-between gap-3 ${aperto ? "mb-4" : ""}`}>
        <button data-testid="scadenze-doc-toggle" onClick={() => setAperto(!aperto)} className="flex items-center gap-2 text-left">
          {aperto ? <ChevronDown className="w-4 h-4 text-[#1A2B4A]" /> : <ChevronRight className="w-4 h-4 text-[#1A2B4A]" />}
          <IdCard className="w-4 h-4 text-[#C9A84C]" />
          <h3 className="text-lg font-semibold tracking-tight text-[#1A2B4A]">Documenti in scadenza</h3>
        </button>
        {aperto && azioni}
      </div>
      )}

      {aperto && (
      <>

      {(tot.scaduto || tot["30"] || tot["60"] || tot["90"]) ? (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="scadenze-doc-badges">
          {["scaduto", "30", "60", "90"].filter((k) => tot[k]).map((k) => (
            <span key={k} className={`text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-md ${LEVELS[k].chip}`}>
              {tot[k]} {LEVELS[k].label}
            </span>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-[#4a5568]">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[#4a5568]" data-testid="scadenze-doc-empty">
          Nessun passaporto, permesso o documento d'identità in scadenza nei prossimi {giorni} giorni
          (vengono considerate le date presenti in anagrafica).
        </p>
      ) : (
        <ul className="divide-y divide-[#1A2B4A]/5 max-h-80 overflow-y-auto" data-testid="scadenze-doc-list">
          {items.map((it) => <Riga key={`${it.cliente_id}-${it.campo}`} it={it} />)}
        </ul>
      )}
      </>
      )}
    </div>
  );
}
